import axios from 'axios';

/**
 * humanizeError() — convert an API error into a user-friendly string for toast.
 * Covers FastAPI validation errors (422), auth errors (401/403), and generic server errors.
 */
export function humanizeError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as Record<string, unknown> | undefined;

    // Friendly, security-sensitive generics that should win regardless of the
    // backend's (often terse / internal) message.
    if (status === 401) return 'Session expired. Please log in again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 429) return 'Too many requests. Please wait a moment and try again.';
    if (status != null && status >= 500) {
      return 'A server error occurred. Please try again or contact support.';
    }

    // FastAPI 422 validation error — extract first message
    if (status === 422 && data && Array.isArray(data.detail)) {
      const first = data.detail[0] as Record<string, unknown> | undefined;
      if (first && typeof first.msg === 'string') {
        return `Validation error: ${first.msg}`;
      }
    }

    // Prefer the backend's SPECIFIC message for client errors (400 / 404 / 409 /
    // …) so e.g. a registration 409 surfaces "An account with this email already
    // exists. Please log in…" instead of a useless generic. App error envelope
    // (global AppError handler): { error_code, message, errors?: [{ loc, msg }] }.
    if (data) {
      const errorsArr = data.errors;
      if (Array.isArray(errorsArr) && errorsArr.length > 0) {
        const msgs = errorsArr
          .map((e) => {
            const rec = e as Record<string, unknown> | undefined;
            return rec && typeof rec.msg === 'string' ? rec.msg : null;
          })
          .filter((m): m is string => Boolean(m));
        if (msgs.length > 0) return msgs.join('; ');
      }
      if (typeof data.message === 'string' && data.message) {
        return data.message;
      }
      // FastAPI detail string
      if (typeof data.detail === 'string') {
        return data.detail;
      }
    }

    // Generic fallbacks only when the backend supplied no specific message.
    if (status === 404) return 'The requested resource was not found.';
    if (status === 409) return 'This action conflicts with the current state. Please refresh.';

    if (err.message) return err.message;
  }

  if (err instanceof Error) return err.message;

  return 'An unexpected error occurred.';
}
